import { useEffect, useState } from "react";
import { ChevronDown, CreditCard, Gift, LogOut, MapPin, Package, Star, Truck } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { api, errMsg, fmt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MaskedLine, Reveal } from "@/components/Reveal";

const STATUS_LABELS = {
  en_attente: "En attente de paiement",
  en_attente_paiement: "En attente de paiement",
  paiement_recu: "Paiement reçu",
  confirmee: "Commande confirmée",
  en_preparation: "En préparation",
  expediee: "Expédiée",
  livree: "Livrée",
  annulee: "Annulée",
};
const PAYMENT_LABELS = { paypal: "PayPal", stripe: "Carte bancaire", virement: "Virement bancaire" };

function trackingUrl(o) {
  if (!o.tracking) return null;
  if (o.shipping?.carrier === "mondial_relay") return `https://www.mondialrelay.fr/suivi-de-colis?numeroExpedition=${encodeURIComponent(o.tracking)}`;
  return null;
}

function OrderCard({ o }) {
  const [open, setOpen] = useState(false);
  const tUrl = trackingUrl(o);
  return (
    <div className="rounded-2xl border border-kay-line overflow-hidden" data-testid={`order-${o.id}`}>
      <button type="button" onClick={() => setOpen(!open)} className="w-full p-5 grid sm:grid-cols-4 gap-3 items-center text-left hover:bg-kay-rose/20 transition-colors" data-testid={`order-toggle-${o.id}`}>
        <div>
          <p className="font-mono text-xs font-bold">{o.number}</p>
          <p className="text-[11px] text-kay-ink/40">{new Date(o.created_at).toLocaleDateString("fr-FR")}</p>
        </div>
        <p className="text-sm text-kay-ink/60 sm:col-span-2 line-clamp-2">
          {o.items.map((i) => `${i.qty}× ${i.name}${i.variant ? ` (${i.variant})` : ""}`).join(", ")}
        </p>
        <div className="flex items-center justify-between sm:justify-end gap-3">
          <span className="font-display text-xl font-semibold">{fmt(o.total)}</span>
          <span className="font-mono text-[10px] uppercase tracking-widest rounded-full bg-kay-rose px-3 py-1 text-kay-wine" data-testid={`order-status-${o.id}`}>
            {STATUS_LABELS[o.status] || o.status}
          </span>
          <motion.span animate={{ rotate: open ? 180 : 0 }}><ChevronDown size={16} className="text-kay-ink/40" /></motion.span>
        </div>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
            <div className="px-5 pb-5 pt-1 border-t border-kay-line grid gap-4 sm:grid-cols-2" data-testid={`order-details-${o.id}`}>
              <div className="grid gap-1.5">
                {o.items.map((i, idx) => (
                  <div key={idx} className="flex justify-between text-sm" data-testid={`order-item-${o.id}-${idx}`}>
                    <span className="text-kay-ink/70">{i.qty}× {i.name}{i.variant && <span className="text-kay-pink"> — {i.variant}</span>}</span>
                    <span className="font-semibold">{fmt(i.price * i.qty)}</span>
                  </div>
                ))}
                <div className="border-t border-kay-line mt-1 pt-2 grid gap-1 text-[13px]">
                  <p className="flex justify-between text-kay-ink/50"><span>Sous-total</span><span>{fmt(o.subtotal)}</span></p>
                  {o.promo_discount > 0 && <p className="flex justify-between text-kay-pink"><span>Code {o.promo_code}</span><span>-{fmt(o.promo_discount)}</span></p>}
                  {o.points_value > 0 && <p className="flex justify-between text-kay-pink"><span>Points fidélité</span><span>-{fmt(o.points_value)}</span></p>}
                  <p className="flex justify-between text-kay-ink/50">
                    <span>Livraison{o.shipping?.method === "relay" ? " (Point Relais)" : o.shipping?.method === "home" ? " (domicile)" : ""}</span>
                    <span>{(o.shipping_cost ?? o.shipping?.price) > 0 ? fmt(o.shipping_cost ?? o.shipping?.price) : "Offerte"}</span>
                  </p>
                  <p className="flex justify-between font-bold text-sm"><span>Total</span><span>{fmt(o.total)}</span></p>
                </div>
                {o.points_earned > 0 && <p className="text-[11px] text-kay-pink font-semibold">+{o.points_earned} points gagnés avec cette commande</p>}
              </div>
              <div className="grid gap-3 content-start text-sm">
                <p className="flex items-center gap-2 text-kay-ink/60"><CreditCard size={14} className="text-kay-pink" /> {PAYMENT_LABELS[o.payment_method] || o.payment_method} {o.paid ? "· payée" : "· en attente"}</p>
                {o.shipping?.relay ? (
                  <div className="flex items-start gap-2 text-kay-ink/60" data-testid={`order-relay-${o.id}`}>
                    <MapPin size={14} className="text-kay-pink mt-0.5 shrink-0" />
                    <span>Point Relais : <strong className="text-kay-ink">{o.shipping.relay.name}</strong><br />{o.shipping.relay.address}, {o.shipping.relay.postcode} {o.shipping.relay.city}</span>
                  </div>
                ) : (
                  <p className="flex items-start gap-2 text-kay-ink/60" data-testid={`order-address-${o.id}`}>
                    <MapPin size={14} className="text-kay-pink mt-0.5 shrink-0" />
                    <span>{o.customer_name}<br />{o.address}{o.address2 ? `, ${o.address2}` : ""}<br />{o.zip} {o.city}{o.country ? `, ${o.country}` : ""}</span>
                  </p>
                )}
                {o.tracking && (
                  <p className="flex items-center gap-2" data-testid={`order-tracking-${o.id}`}>
                    <Truck size={14} className="text-kay-pink" />
                    {tUrl ? (
                      <a href={tUrl} target="_blank" rel="noopener noreferrer" className="text-kay-pink font-semibold underline underline-offset-2">Suivre mon colis ({o.tracking})</a>
                    ) : (
                      <span className="text-kay-ink/60">N° de suivi : <strong className="font-mono text-xs">{o.tracking}</strong></span>
                    )}
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function AuthForm() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
      toast.success(mode === "login" ? "Ravie de te revoir !" : "Bienvenue chez KAY BEAUTY !");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Reveal className="max-w-md mx-auto">
      <form onSubmit={submit} className="rounded-3xl border border-kay-line bg-white p-8 lg:p-10 grid gap-4 shadow-[0_10px_30px_rgba(232,160,191,0.12)]" data-testid="auth-form">
        <div className="flex rounded-full border border-kay-line p-1 mb-2">
          {[
            { v: "login", label: "Connexion" },
            { v: "register", label: "Créer un compte" },
          ].map((t) => (
            <button
              type="button"
              key={t.v}
              data-testid={`auth-tab-${t.v}`}
              onClick={() => setMode(t.v)}
              className={`flex-1 rounded-full py-2 text-xs font-bold transition-all ${mode === t.v ? "bg-kay-ink text-white" : ""}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {mode === "register" && (
          <input required data-testid="auth-name-input" placeholder="Ton prénom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl border border-kay-line px-4 py-3 text-sm outline-none focus:border-kay-pink" />
        )}
        <input required type="email" data-testid="auth-email-input" placeholder="Ton email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl border border-kay-line px-4 py-3 text-sm outline-none focus:border-kay-pink" />
        <input required type="password" minLength={6} data-testid="auth-password-input" placeholder="Mot de passe (6 caractères min.)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-xl border border-kay-line px-4 py-3 text-sm outline-none focus:border-kay-pink" />
        <button data-testid="auth-submit-button" type="submit" disabled={loading} className="rounded-full bg-kay-ink text-white py-3.5 text-sm font-bold hover:bg-kay-pink transition-colors disabled:opacity-50">
          {loading ? "Un instant…" : mode === "login" ? "Me connecter" : "Créer mon compte"}
        </button>
        <div className="flex items-center gap-3 text-[11px] text-kay-ink/40">
          <span className="flex-1 border-t border-kay-line" /> ou <span className="flex-1 border-t border-kay-line" />
        </div>
        <button
          type="button"
          data-testid="google-login-button"
          onClick={() => {
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            const redirectUrl = window.location.origin + "/compte";
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }}
          className="rounded-full border border-kay-ink py-3.5 text-sm font-bold flex items-center justify-center gap-2 hover:bg-kay-ink hover:text-white transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#EA4335" d="M12 5.04c1.62 0 3.06.56 4.2 1.64l3.12-3.12C17.46 1.8 14.96.75 12 .75 7.62.75 3.84 3.27 1.98 6.93l3.66 2.84C6.55 7.06 9.03 5.04 12 5.04z"/><path fill="#4285F4" d="M23.25 12.26c0-.79-.07-1.54-.19-2.26H12v4.51h6.32c-.27 1.45-1.1 2.68-2.34 3.5l3.6 2.79c2.11-1.95 3.67-4.82 3.67-8.54z"/><path fill="#FBBC05" d="M5.66 14.23a7.2 7.2 0 0 1 0-4.46L1.98 6.93a11.25 11.25 0 0 0 0 10.14l3.68-2.84z"/><path fill="#34A853" d="M12 23.25c3.04 0 5.6-1 7.46-2.72l-3.6-2.79c-1 .68-2.29 1.08-3.86 1.08-2.97 0-5.45-2.02-6.36-4.73l-3.66 2.84c1.86 3.66 5.64 6.32 10.02 6.32z"/></svg>
          Continuer avec Google
        </button>
        <p className="text-[11px] text-kay-ink/40 text-center">
          Ton compte te fait gagner des points de fidélité à chaque commande.
        </p>
      </form>
    </Reveal>
  );
}

export default function Account() {
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) api.get("/orders/my").then((r) => setOrders(r.data)).catch(() => {});
  }, [user]);

  if (user === undefined) return <div className="py-32 text-center font-display italic text-2xl text-kay-ink/40">Chargement…</div>;

  if (!user)
    return (
      <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-10" data-testid="account-page">
        <div className="text-center mb-12">
          <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-kay-pink mb-6">Mon compte</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.1}>Ton espace</MaskedLine>
            <MaskedLine delay={0.25} className="italic text-kay-pink">beauté.</MaskedLine>
          </h1>
        </div>
        <AuthForm />
      </div>
    );

  const points = user.loyalty_points || 0;
  const block = settings?.points_block || 100;
  const value = settings?.block_value || 5;
  const progress = Math.min(100, ((points % block) / block) * 100);

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-14 pb-10" data-testid="account-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-kay-pink mb-3">Mon compte</p>
          <h1 className="font-display text-4xl sm:text-5xl" data-testid="account-greeting">
            Bonjour <span className="italic text-kay-pink">{user.name || "beauty"}</span>
          </h1>
          <p className="mt-2 text-sm text-kay-ink/50">{user.email}</p>
        </div>
        <button data-testid="logout-button" onClick={logout} className="flex items-center gap-2 rounded-full border border-kay-line px-5 py-2.5 text-xs font-bold hover:border-kay-ink transition-colors">
          <LogOut size={14} /> Déconnexion
        </button>
      </div>

      <div className="mt-10 grid lg:grid-cols-3 gap-6">
        <Reveal className="lg:col-span-1">
          <div className="rounded-3xl bg-kay-ink text-kay-cream p-8 grain relative overflow-hidden h-full" data-testid="loyalty-card">
            <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-kay-rosedeep mb-6">
              <Star size={14} /> Programme fidélité
            </div>
            <p className="font-display text-6xl" data-testid="loyalty-points">{points}</p>
            <p className="text-white/50 text-sm mt-1">points cumulés</p>
            <div className="mt-6 h-1.5 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-kay-rosedeep rounded-full transition-all" style={{ width: `${progress}%` }} data-testid="loyalty-progress" />
            </div>
            <p className="mt-3 text-xs text-white/50">
              {block} points = {fmt(value)} de réduction. {settings ? `${settings.points_per_euro} pt gagné par € d'achat.` : ""}
            </p>
            <div className="mt-6 flex items-center gap-2 text-xs text-kay-rosedeep">
              <Gift size={14} /> Utilise tes points au moment du paiement.
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-2">
          <div className="rounded-3xl border border-kay-line bg-white p-8" data-testid="orders-section">
            <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-kay-ink/50 mb-6">
              <Package size={14} /> Mes commandes
            </div>
            {orders.length === 0 ? (
              <p className="font-display italic text-2xl text-kay-ink/40 py-8 text-center">Pas encore de commande — les pépites t'attendent.</p>
            ) : (
              <div className="grid gap-3">
                {orders.map((o) => (
                  <OrderCard key={o.id} o={o} />
                ))}
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </div>
  );
}
